import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Building2,
  ArrowLeft,
  Users,
  Monitor,
  Shield,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  RefreshCw,
  CheckCircle,
  Settings,
  Zap,
  Globe,
  Plus,
  Trash2,
  Key,
  Lock,
  Unlock,
  Edit,
  Save,
  X,
  Power,
  PowerOff,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import MSPLayout from '../components/MSPLayout';
import {
  getMSPClientDetail,
  updateMSPClient,
  getMSPClientUsers,
  addMSPClientUser,
  removeMSPClientUser,
  resetMSPClientUserPassword,
  getMSPClientDevices,
  mspClientDeviceAction,
  getMSPClientAutomationRules,
  createMSPClientAutomationRule,
  updateMSPClientAutomationRule,
  deleteMSPClientAutomationRule,
  getMSPClientDNSPolicies,
  createMSPClientDNSPolicy,
  deleteMSPClientDNSPolicy,
  getMSPClientSecuritySettings,
  updateMSPClientSecuritySettings,
} from '../services/api';
import { toast } from 'sonner';

const healthStatusColors = {
  healthy: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  warning: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  critical: 'bg-red-500/10 text-red-500 border-red-500/20',
};

const planColors = {
  professional: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  enterprise: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
};

const statusColors = {
  active: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  suspended: 'bg-red-500/10 text-red-500 border-red-500/20',
  locked: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
};

export default function MSPClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [automationRules, setAutomationRules] = useState([]);
  const [dnsPolicies, setDnsPolicies] = useState([]);
  const [securitySettings, setSecuritySettings] = useState({});
  
  // Dialog states
  const [showEditClient, setShowEditClient] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showAddRule, setShowAddRule] = useState(false);
  const [showAddPolicy, setShowAddPolicy] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteType, setDeleteType] = useState('');
  
  // Form states
  const [editForm, setEditForm] = useState({ company_name: '', plan: '', seats_included: 5, status: 'active' });
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'analyst' });
  const [ruleForm, setRuleForm] = useState({ name: '', trigger_type: 'dns_block', condition: '', action: 'notify', enabled: true });
  const [policyForm, setPolicyForm] = useState({ name: '', domain_pattern: '', action: 'block', category: 'custom' });
  const [tempPassword, setTempPassword] = useState('');

  useEffect(() => {
    fetchClient();
  }, [clientId]);

  useEffect(() => {
    if (client && activeTab === 'users') fetchUsers();
    if (client && activeTab === 'devices') fetchDevices();
    if (client && activeTab === 'automation') { fetchAutomationRules(); fetchDnsPolicies(); }
    if (client && activeTab === 'security') fetchSecuritySettings();
  }, [activeTab, client]);

  const fetchClient = async () => {
    setLoading(true);
    try {
      const response = await getMSPClientDetail(clientId);
      setClient(response.data);
      const clientInfo = response.data.client || {};
      setEditForm({
        company_name: clientInfo.company_name || '',
        plan: clientInfo.plan || 'professional',
        seats_included: clientInfo.seats_included || 5,
        status: clientInfo.status || 'active'
      });
    } catch (error) {
      console.error('Failed to fetch client:', error);
      toast.error('Failed to load client details');
      navigate('/msp/clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getMSPClientUsers(clientId);
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await getMSPClientDevices(clientId);
      setDevices(response.data.devices || []);
    } catch (error) {
      toast.error('Failed to load devices');
    }
  };

  const fetchAutomationRules = async () => {
    try {
      const response = await getMSPClientAutomationRules(clientId);
      setAutomationRules(response.data.rules || []);
    } catch (error) {
      toast.error('Failed to load automation rules');
    }
  };

  const fetchDnsPolicies = async () => {
    try {
      const response = await getMSPClientDNSPolicies(clientId);
      setDnsPolicies(response.data.policies || []);
    } catch (error) {
      toast.error('Failed to load DNS policies');
    }
  };

  const fetchSecuritySettings = async () => {
    try {
      const response = await getMSPClientSecuritySettings(clientId);
      setSecuritySettings(response.data);
    } catch (error) {
      toast.error('Failed to load security settings');
    }
  };

  // Action Handlers
  const handleUpdateClient = async () => {
    try {
      await updateMSPClient(clientId, editForm);
      toast.success('Client updated successfully');
      setShowEditClient(false);
      fetchClient();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    }
  };

  const handleAddUser = async () => {
    try {
      await addMSPClientUser(clientId, userForm);
      toast.success('User added successfully');
      setShowAddUser(false);
      setUserForm({ name: '', email: '', password: '', role: 'analyst' });
      fetchUsers();
      fetchClient();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    }
  };

  const handleRemoveUser = async () => {
    try {
      await removeMSPClientUser(clientId, selectedItem.id);
      toast.success('User removed successfully');
      setShowDeleteConfirm(false);
      setSelectedItem(null);
      fetchUsers();
      fetchClient();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    }
  };

  const handleResetPassword = async () => {
    try {
      const response = await resetMSPClientUserPassword(clientId, selectedItem.id);
      setTempPassword(response.data.temporary_password);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDeviceAction = async (deviceId, action) => {
    try {
      await mspClientDeviceAction(clientId, deviceId, action);
      toast.success(`Device ${action}ed successfully`);
      fetchDevices();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} device`);
    }
  };

  const handleAddRule = async () => {
    try {
      await createMSPClientAutomationRule(clientId, ruleForm);
      toast.success('Automation rule created');
      setShowAddRule(false);
      setRuleForm({ name: '', trigger_type: 'dns_block', condition: '', action: 'notify', enabled: true });
      fetchAutomationRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rule');
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      await updateMSPClientAutomationRule(clientId, rule.id, { ...rule, enabled: !rule.enabled });
      toast.success(`Rule ${rule.enabled ? 'disabled' : 'enabled'}`);
      fetchAutomationRules();
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  const handleDeleteRule = async () => {
    try {
      await deleteMSPClientAutomationRule(clientId, selectedItem.id);
      toast.success('Rule deleted');
      setShowDeleteConfirm(false);
      setSelectedItem(null);
      fetchAutomationRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const handleAddPolicy = async () => {
    try {
      await createMSPClientDNSPolicy(clientId, policyForm);
      toast.success('DNS policy created');
      setShowAddPolicy(false);
      setPolicyForm({ name: '', domain_pattern: '', action: 'block', category: 'custom' });
      fetchDnsPolicies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create policy');
    }
  };

  const handleDeletePolicy = async () => {
    try {
      await deleteMSPClientDNSPolicy(clientId, selectedItem.id);
      toast.success('Policy deleted');
      setShowDeleteConfirm(false);
      setSelectedItem(null);
      fetchDnsPolicies();
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  const handleUpdateSecuritySettings = async () => {
    try {
      await updateMSPClientSecuritySettings(clientId, securitySettings);
      toast.success('Security settings updated');
    } catch (error) {
      toast.error('Failed to update security settings');
    }
  };

  const handleDelete = () => {
    if (deleteType === 'user') handleRemoveUser();
    else if (deleteType === 'rule') handleDeleteRule();
    else if (deleteType === 'policy') handleDeletePolicy();
  };

  if (loading) {
    return (
      <MSPLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      </MSPLayout>
    );
  }

  if (!client) {
    return (
      <MSPLayout>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-heading text-white mb-2">Client Not Found</h2>
          <Button onClick={() => navigate('/msp/clients')} variant="outline" className="border-slate-700">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Clients
          </Button>
        </div>
      </MSPLayout>
    );
  }

  const clientInfo = client.client || {};
  const usersInfo = client.users || {};
  const devicesInfo = client.devices || {};
  const security = client.security || {};
  const billing = client.billing || {};

  return (
    <MSPLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/msp/clients')} className="mt-1">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="font-bold text-2xl text-white">{clientInfo.company_name}</h1>
                <Badge className={planColors[clientInfo.plan] || planColors.professional}>
                  {clientInfo.plan || 'professional'}
                </Badge>
                <Badge className={statusColors[clientInfo.status] || statusColors.active}>
                  {clientInfo.status === 'active' && <CheckCircle className="w-3 h-3 mr-1" />}
                  {clientInfo.status === 'suspended' && <PowerOff className="w-3 h-3 mr-1" />}
                  {clientInfo.status || 'active'}
                </Badge>
              </div>
              <p className="text-sm text-slate-400">
                Client since {new Date(clientInfo.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowEditClient(true)} className="border-slate-700">
              <Edit className="w-4 h-4 mr-2" />
              Edit Client
            </Button>
            <Button variant="outline" onClick={fetchClient} className="border-slate-700">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{usersInfo.total || 0}</p>
                  <p className="text-xs text-slate-500">Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Monitor className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{devicesInfo.total || 0}</p>
                  <p className="text-xs text-slate-500">Devices</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{security.threats_blocked?.total || 0}</p>
                  <p className="text-xs text-slate-500">Threats Blocked</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-cyan-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">${billing.monthly_amount || 0}</p>
                  <p className="text-xs text-slate-500">Monthly</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-400">+${billing.commission?.toFixed(2) || 0}</p>
                  <p className="text-xs text-slate-500">Your Commission</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-slate-900 border border-slate-800 mb-6">
            <TabsTrigger value="overview" className="data-[state=active]:bg-slate-800">
              <Building2 className="w-4 h-4 mr-2" />Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-slate-800">
              <Users className="w-4 h-4 mr-2" />Users
            </TabsTrigger>
            <TabsTrigger value="devices" className="data-[state=active]:bg-slate-800">
              <Monitor className="w-4 h-4 mr-2" />Devices
            </TabsTrigger>
            <TabsTrigger value="automation" className="data-[state=active]:bg-slate-800">
              <Zap className="w-4 h-4 mr-2" />Automation
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-slate-800">
              <Shield className="w-4 h-4 mr-2" />Security
            </TabsTrigger>
            <TabsTrigger value="billing" className="data-[state=active]:bg-slate-800">
              <DollarSign className="w-4 h-4 mr-2" />Billing
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-lg text-white">Client Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between py-2 border-b border-slate-800">
                    <span className="text-slate-400">Company Name</span>
                    <span className="text-white font-medium">{clientInfo.company_name}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-800">
                    <span className="text-slate-400">Plan</span>
                    <Badge className={planColors[clientInfo.plan] || planColors.professional}>
                      {clientInfo.plan || 'professional'}
                    </Badge>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-800">
                    <span className="text-slate-400">Seats</span>
                    <span className="text-white">
                      {usersInfo.total || 0} / {clientInfo.seats_included || 5}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-800">
                    <span className="text-slate-400">Status</span>
                    <Badge className={statusColors[clientInfo.status] || statusColors.active}>
                      {clientInfo.status || 'active'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-lg text-white">Security Overview</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between py-2 border-b border-slate-800">
                    <span className="text-slate-400">Risk Score</span>
                    <span className={`font-bold ${security.risk_score >= 75 ? 'text-red-500' : security.risk_score >= 50 ? 'text-amber-500' : 'text-emerald-500'}`}>
                      {security.risk_score || 0}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-800">
                    <span className="text-slate-400">DNS Blocked</span>
                    <span className="text-white">{security.threats_blocked?.dns || 0}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-800">
                    <span className="text-slate-400">Files Quarantined</span>
                    <span className="text-white">{security.threats_blocked?.files || 0}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-800">
                    <span className="text-slate-400">Emails Blocked</span>
                    <span className="text-white">{security.threats_blocked?.emails || 0}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-white">User Management</h3>
                <p className="text-sm text-slate-400">Manage users for this client</p>
              </div>
              <Button onClick={() => setShowAddUser(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />Add User
              </Button>
            </div>
            <Card className="bg-slate-900 border-slate-800">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Name</TableHead>
                    <TableHead className="text-slate-400">Email</TableHead>
                    <TableHead className="text-slate-400">Role</TableHead>
                    <TableHead className="text-slate-400">Created</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow key={user.id} className="border-slate-800">
                        <TableCell className="text-white font-medium">{user.name}</TableCell>
                        <TableCell className="text-slate-400">{user.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-slate-700">{user.role}</Badge>
                        </TableCell>
                        <TableCell className="text-slate-400">
                          {new Date(user.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setSelectedItem(user); setShowResetPassword(true); }}
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-400"
                              onClick={() => { setSelectedItem(user); setDeleteType('user'); setShowDeleteConfirm(true); }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Devices Tab */}
          <TabsContent value="devices" className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white">Device Management</h3>
              <p className="text-sm text-slate-400">View and manage client devices</p>
            </div>
            <Card className="bg-slate-900 border-slate-800">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Hostname</TableHead>
                    <TableHead className="text-slate-400">OS</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Risk Score</TableHead>
                    <TableHead className="text-slate-400">Last Seen</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                        No devices found
                      </TableCell>
                    </TableRow>
                  ) : (
                    devices.map((device) => (
                      <TableRow key={device.id} className="border-slate-800">
                        <TableCell className="text-white font-medium">{device.hostname}</TableCell>
                        <TableCell className="text-slate-400">{device.os_type} {device.os_version}</TableCell>
                        <TableCell>
                          <Badge className={statusColors[device.status] || statusColors.active}>
                            {device.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className={device.risk_score >= 50 ? 'text-red-500' : 'text-emerald-500'}>
                            {device.risk_score}
                          </span>
                        </TableCell>
                        <TableCell className="text-slate-400">
                          {new Date(device.last_seen).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {device.status === 'locked' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeviceAction(device.id, 'unlock')}
                              className="border-emerald-700 text-emerald-500"
                            >
                              <Unlock className="w-4 h-4 mr-1" />Unlock
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeviceAction(device.id, 'lock')}
                              className="border-amber-700 text-amber-500"
                            >
                              <Lock className="w-4 h-4 mr-1" />Lock
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Automation Tab */}
          <TabsContent value="automation" className="space-y-6">
            {/* Automation Rules */}
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-white">Automation Rules</h3>
                <p className="text-sm text-slate-400">Manage automated security responses</p>
              </div>
              <Button onClick={() => setShowAddRule(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />Add Rule
              </Button>
            </div>
            <Card className="bg-slate-900 border-slate-800">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Name</TableHead>
                    <TableHead className="text-slate-400">Trigger</TableHead>
                    <TableHead className="text-slate-400">Condition</TableHead>
                    <TableHead className="text-slate-400">Action</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {automationRules.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                        No automation rules found
                      </TableCell>
                    </TableRow>
                  ) : (
                    automationRules.map((rule) => (
                      <TableRow key={rule.id} className="border-slate-800">
                        <TableCell className="text-white font-medium">{rule.name}</TableCell>
                        <TableCell className="text-slate-400">{rule.trigger_type}</TableCell>
                        <TableCell className="text-slate-400 font-mono text-xs">{rule.condition}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-slate-700">{rule.action}</Badge>
                        </TableCell>
                        <TableCell>
                          <Switch checked={rule.enabled} onCheckedChange={() => handleToggleRule(rule)} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-400"
                            onClick={() => { setSelectedItem(rule); setDeleteType('rule'); setShowDeleteConfirm(true); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>

            {/* DNS Policies */}
            <div className="flex justify-between items-center mt-8">
              <div>
                <h3 className="text-lg font-semibold text-white">DNS Policies</h3>
                <p className="text-sm text-slate-400">Custom domain blocking rules</p>
              </div>
              <Button onClick={() => setShowAddPolicy(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />Add Policy
              </Button>
            </div>
            <Card className="bg-slate-900 border-slate-800">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Name</TableHead>
                    <TableHead className="text-slate-400">Domain Pattern</TableHead>
                    <TableHead className="text-slate-400">Action</TableHead>
                    <TableHead className="text-slate-400">Category</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dnsPolicies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                        No DNS policies found
                      </TableCell>
                    </TableRow>
                  ) : (
                    dnsPolicies.map((policy) => (
                      <TableRow key={policy.id} className="border-slate-800">
                        <TableCell className="text-white font-medium">{policy.name}</TableCell>
                        <TableCell className="text-slate-400 font-mono text-xs">{policy.domain_pattern}</TableCell>
                        <TableCell>
                          <Badge className={policy.action === 'block' ? 'bg-red-500/10 text-red-500' : 'bg-emerald-500/10 text-emerald-500'}>
                            {policy.action}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-400">{policy.category}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-400"
                            onClick={() => { setSelectedItem(policy); setDeleteType('policy'); setShowDeleteConfirm(true); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white">Security Settings</h3>
              <p className="text-sm text-slate-400">Configure security policies for this client</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">DNS Protection</CardTitle>
                  <CardDescription>Control DNS filtering behavior</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">Block Malicious Domains</Label>
                    <Switch
                      checked={securitySettings.block_malicious_domains ?? true}
                      onCheckedChange={(v) => setSecuritySettings({...securitySettings, block_malicious_domains: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">Block Newly Registered Domains</Label>
                    <Switch
                      checked={securitySettings.block_newly_registered ?? true}
                      onCheckedChange={(v) => setSecuritySettings({...securitySettings, block_newly_registered: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">Block Suspicious Categories</Label>
                    <Switch
                      checked={securitySettings.block_suspicious_categories ?? true}
                      onCheckedChange={(v) => setSecuritySettings({...securitySettings, block_suspicious_categories: v})}
                    />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">Email Protection</CardTitle>
                  <CardDescription>Configure email security settings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">Auto-quarantine Phishing</Label>
                    <Switch
                      checked={securitySettings.auto_quarantine_phishing ?? true}
                      onCheckedChange={(v) => setSecuritySettings({...securitySettings, auto_quarantine_phishing: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">Scan Attachments</Label>
                    <Switch
                      checked={securitySettings.scan_attachments ?? true}
                      onCheckedChange={(v) => setSecuritySettings({...securitySettings, scan_attachments: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">URL Rewriting</Label>
                    <Switch
                      checked={securitySettings.url_rewriting ?? true}
                      onCheckedChange={(v) => setSecuritySettings({...securitySettings, url_rewriting: v})}
                    />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">Notifications</CardTitle>
                  <CardDescription>Alert and reporting preferences</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">Critical Threat Alerts</Label>
                    <Switch
                      checked={securitySettings.critical_threat_alerts ?? true}
                      onCheckedChange={(v) => setSecuritySettings({...securitySettings, critical_threat_alerts: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">Weekly Reports</Label>
                    <Switch
                      checked={securitySettings.weekly_reports ?? true}
                      onCheckedChange={(v) => setSecuritySettings({...securitySettings, weekly_reports: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">Device Status Alerts</Label>
                    <Switch
                      checked={securitySettings.device_status_alerts ?? false}
                      onCheckedChange={(v) => setSecuritySettings({...securitySettings, device_status_alerts: v})}
                    />
                  </div>
                </CardContent>
              </Card>
              <div className="flex items-end">
                <Button onClick={handleUpdateSecuritySettings} className="bg-blue-600 hover:bg-blue-700 w-full">
                  <Save className="w-4 h-4 mr-2" />Save Security Settings
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" className="space-y-6">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Billing Information</CardTitle>
                <CardDescription>Subscription and commission details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between py-2 border-b border-slate-800">
                  <span className="text-slate-400">Plan</span>
                  <Badge className={planColors[billing.plan] || planColors.professional}>
                    {billing.plan || 'professional'}
                  </Badge>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-800">
                  <span className="text-slate-400">Monthly Amount</span>
                  <span className="text-white font-medium">${billing.monthly_amount || 299}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-800">
                  <span className="text-slate-400">Your Commission (30%)</span>
                  <span className="text-emerald-400 font-bold">+${billing.commission?.toFixed(2) || 0}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-800">
                  <span className="text-slate-400">Next Billing Date</span>
                  <span className="text-white">{billing.next_billing || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-400">Payment Status</span>
                  <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                    {billing.status || 'paid'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Client Dialog */}
      <Dialog open={showEditClient} onOpenChange={setShowEditClient}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Client</DialogTitle>
            <DialogDescription>Update client settings and status</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Company Name</Label>
              <Input
                value={editForm.company_name}
                onChange={(e) => setEditForm({...editForm, company_name: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Plan</Label>
              <Select value={editForm.plan} onValueChange={(v) => setEditForm({...editForm, plan: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="professional">Professional ($299/mo)</SelectItem>
                  <SelectItem value="enterprise">Enterprise ($599/mo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Seats Included</Label>
              <Input
                type="number"
                min="1"
                value={editForm.seats_included}
                onChange={(e) => setEditForm({...editForm, seats_included: parseInt(e.target.value)})}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({...editForm, status: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditClient(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button onClick={handleUpdateClient} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Add User</DialogTitle>
            <DialogDescription>Create a new user for this client</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Name</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Email</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="john@company.com"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Password</Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="Min 8 characters"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Role</Label>
              <Select value={userForm.role} onValueChange={(v) => setUserForm({...userForm, role: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="analyst">Analyst</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUser(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button onClick={handleAddUser} className="bg-blue-600 hover:bg-blue-700">
              Add User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Automation Rule Dialog */}
      <Dialog open={showAddRule} onOpenChange={setShowAddRule}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Add Automation Rule</DialogTitle>
            <DialogDescription>Create a new automation rule for this client</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Rule Name</Label>
              <Input
                value={ruleForm.name}
                onChange={(e) => setRuleForm({...ruleForm, name: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="Block high-risk files"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Trigger Type</Label>
              <Select value={ruleForm.trigger_type} onValueChange={(v) => setRuleForm({...ruleForm, trigger_type: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="dns_block">DNS Block</SelectItem>
                  <SelectItem value="file_malicious">Malicious File</SelectItem>
                  <SelectItem value="email_phishing">Phishing Email</SelectItem>
                  <SelectItem value="app_anomaly">App Anomaly</SelectItem>
                  <SelectItem value="high_risk_score">High Risk Score</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Condition</Label>
              <Input
                value={ruleForm.condition}
                onChange={(e) => setRuleForm({...ruleForm, condition: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white font-mono text-sm"
                placeholder="risk_score >= 80"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Action</Label>
              <Select value={ruleForm.action} onValueChange={(v) => setRuleForm({...ruleForm, action: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="notify">Notify</SelectItem>
                  <SelectItem value="quarantine">Quarantine</SelectItem>
                  <SelectItem value="block">Block</SelectItem>
                  <SelectItem value="lock_device">Lock Device</SelectItem>
                  <SelectItem value="send_alert">Send Alert</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRule(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button onClick={handleAddRule} className="bg-blue-600 hover:bg-blue-700">
              Create Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add DNS Policy Dialog */}
      <Dialog open={showAddPolicy} onOpenChange={setShowAddPolicy}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Add DNS Policy</DialogTitle>
            <DialogDescription>Create a custom DNS blocking rule</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Policy Name</Label>
              <Input
                value={policyForm.name}
                onChange={(e) => setPolicyForm({...policyForm, name: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="Block social media"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Domain Pattern</Label>
              <Input
                value={policyForm.domain_pattern}
                onChange={(e) => setPolicyForm({...policyForm, domain_pattern: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white font-mono text-sm"
                placeholder="*.facebook.com"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Action</Label>
              <Select value={policyForm.action} onValueChange={(v) => setPolicyForm({...policyForm, action: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="block">Block</SelectItem>
                  <SelectItem value="allow">Allow</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Category</Label>
              <Select value={policyForm.category} onValueChange={(v) => setPolicyForm({...policyForm, category: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="custom">Custom</SelectItem>
                  <SelectItem value="social_media">Social Media</SelectItem>
                  <SelectItem value="streaming">Streaming</SelectItem>
                  <SelectItem value="gaming">Gaming</SelectItem>
                  <SelectItem value="adult">Adult Content</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPolicy(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button onClick={handleAddPolicy} className="bg-blue-600 hover:bg-blue-700">
              Create Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPassword} onOpenChange={(v) => { setShowResetPassword(v); if (!v) setTempPassword(''); }}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Reset Password</DialogTitle>
            <DialogDescription>
              {tempPassword 
                ? 'Password has been reset. Share this temporary password with the user.'
                : `Reset password for ${selectedItem?.name}?`
              }
            </DialogDescription>
          </DialogHeader>
          {tempPassword ? (
            <div className="py-4">
              <div className="p-4 rounded-lg bg-slate-800 border border-slate-700">
                <Label className="text-slate-400 text-xs">Temporary Password</Label>
                <p className="text-white font-mono text-lg mt-1">{tempPassword}</p>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                The user will need to change this password upon login.
              </p>
            </div>
          ) : null}
          <DialogFooter>
            {tempPassword ? (
              <Button onClick={() => { setShowResetPassword(false); setTempPassword(''); setSelectedItem(null); }} className="bg-blue-600 hover:bg-blue-700">
                Done
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setShowResetPassword(false); setSelectedItem(null); }} className="border-slate-700">
                  Cancel
                </Button>
                <Button onClick={handleResetPassword} className="bg-amber-600 hover:bg-amber-700">
                  Reset Password
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteType === 'user' && `This will permanently remove ${selectedItem?.name} from this client.`}
              {deleteType === 'rule' && `This will delete the automation rule "${selectedItem?.name}".`}
              {deleteType === 'policy' && `This will delete the DNS policy "${selectedItem?.name}".`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MSPLayout>
  );
}
